import React, { type PropsWithChildren, useMemo } from 'react';

import { fg } from '@atlaskit/platform-feature-flags';

import {
	type BootstrapState,
	type ClearReferrerCallback,
	initialState,
	type OnSearchCompletedCallback,
	type RefreshSearchSessionIdCallback,
	type SetThirdPartyConfigsCallback,
} from '../../../../../common/constants/bootstrap';
import { ALL_FILTER_EXCLUDED_PRODUCTS } from '../../../../../common/constants/filters';
import {
	SearchExperience,
	type SearchStoreExperience,
} from '../../../../../common/constants/search';
import { parseQueryParams } from '../../../../../common/utils/query-params';
import { type BootstrapContainerProps, type QueryParamsCallback } from '../../index';
import { StaticBootstrapContext } from '../index';

export type StaticContextProviderProps = {
	refreshSearchSessionId: RefreshSearchSessionIdCallback;
	clearReferrer: ClearReferrerCallback;
	initialText: string | undefined;
	setThirdPartyConfigs: SetThirdPartyConfigsCallback;
	experience?: SearchStoreExperience;
};

export type StaticBootstrapContextState = Omit<
	BootstrapState,
	| 'queryParams'
	| 'referrer'
	| 'queryParamsCallback'
	| 'searchSessionId'
	| 'smartLinkUsage'
	| 'thirdPartyConfigs'
> & {
	// Added the callback wasnt being stored in state (good) and now is in static context, and is not undefined, because we always set noop in state initialiser
	queryParamsCallback: QueryParamsCallback;
	refreshSearchSessionId: RefreshSearchSessionIdCallback;
	clearReferrer: ClearReferrerCallback;
	initialTextQueryParam: string | undefined;
	setThirdPartyConfigs: SetThirdPartyConfigsCallback;
	onSearchCompleted: OnSearchCompletedCallback;
	experience: SearchStoreExperience;
};

// Attempting to build defaultstate from the same underlying definitions to ease migration to minimise migration
// omit from initialState
// eslint-disable-next-line no-unused-vars
const { queryParams, referrer, searchSessionId, thirdPartyConfigs, ...inheritedInitialState } =
	initialState;
export const defaultStaticContext = {
	...inheritedInitialState,
	initialTextQueryParam: undefined,
	queryParamsCallback: () => {},
	refreshSearchSessionId: () => {},
	clearReferrer: () => {},
	setThirdPartyConfigs: () => {},
	onSearchCompleted: () => {},
	experience: SearchExperience.SEARCH_PAGE as SearchStoreExperience,
};

/**
 * Conceptually this is a large single useMemo around as many pieces of state as we can guarantee do not change after the bootstrap phase (isConfigLoaded)
 */
export const StaticContextProvider = ({
	children,
	initialProps: props,
	queryParams,
	queryParamsCallback,
	refreshSearchSessionId,
	initialText,
	clearReferrer,
	setThirdPartyConfigs,
	onSearchCompleted,
	experience,
}: PropsWithChildren<BootstrapContainerProps & StaticContextProviderProps>): React.JSX.Element => {
	// Do not change this! This is what enforces only a single rerender when config is ready
	const staticLock = props.isConfigLoaded;
	const parsedQueryParams =
		queryParams && fg('enable_rovo_search_overrides') ? parseQueryParams(queryParams) : undefined;

	const useMemoStaticProps: StaticBootstrapContextState = useMemo(() => {
		return {
			cloudId: props.cloudId || defaultStaticContext.cloudId,
			aggregatorHostName: props.aggregatorHostName || defaultStaticContext.aggregatorHostName,
			aggAbsoluteUrl: props.aggAbsoluteUrl || defaultStaticContext.aggAbsoluteUrl,
			SAINHostName: props.SAINHostName || defaultStaticContext.SAINHostName,
			orgId: props.orgId || defaultStaticContext.orgId,
			primaryProduct: props.primaryProduct || defaultStaticContext.primaryProduct,
			analyticsAttributes: props.analyticsAttributes || defaultStaticContext.analyticsAttributes,
			features: props.features || defaultStaticContext.features,
			intercomHmac: props.intercomHmac || defaultStaticContext.intercomHmac,
			experimentConfig: props.experimentConfig || defaultStaticContext.experimentConfig,
			experimentLayers: props.experimentLayers || defaultStaticContext.experimentLayers,
			isNounsAvailable: props.isNounsAvailable || defaultStaticContext.isNounsAvailable,
			isRovoEnabled: props.isRovoEnabled || defaultStaticContext.isRovoEnabled,
			isConfigLoaded: props.isConfigLoaded || defaultStaticContext.isConfigLoaded,
			nouns: props.nouns || defaultStaticContext.nouns,
			allFilterExcludedProducts: ALL_FILTER_EXCLUDED_PRODUCTS,
			productEdition: props.productEdition || defaultStaticContext.productEdition,
			productDataRegions: props.productDataRegions || defaultStaticContext.productDataRegions,
			rovoFeatures: props.rovoFeatures || defaultStaticContext.rovoFeatures,
			linkedCloudARIs: props.linkedCloudARIs || defaultStaticContext.linkedCloudARIs,
			isJiraIssueIndexed: props.isJiraIssueIndexed || defaultStaticContext.isJiraIssueIndexed,
			user: props.user || defaultStaticContext.user,
			products: props.products || defaultStaticContext.products,
			initialSpaceARIs: props.initialSpaceARIs || defaultStaticContext.initialSpaceARIs,
			hasEditOrViewPlanPermission: props.hasEditOrViewPlanPermission || defaultStaticContext.hasEditOrViewPlanPermission,
			initialTextQueryParam: initialText,
			onSearchCompleted:
				onSearchCompleted || props.onSearchCompleted || defaultStaticContext.onSearchCompleted,
			experience: experience || defaultStaticContext.experience,
			// when removing this feature flag, update the type to no longer be optional
			...(fg('enable_rovo_search_overrides') && {
				overrides: parsedQueryParams?.overrides || initialState.overrides,
			}),
			// Callbacks are considered statically defined too!
			queryParamsCallback: queryParamsCallback || defaultStaticContext.queryParamsCallback,
			// passed directly through
			refreshSearchSessionId,
			clearReferrer,
			setThirdPartyConfigs,
			// we should no longer need this, as it was part of syncing the query params into the bootstrap store and not used elsewhere. Remove when cleaning up rovo-search-remove-rss-bootstrap-fg
			prevQueryParams: {},
		};
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [staticLock]);

	return (
		<StaticBootstrapContext.Provider value={useMemoStaticProps}>
			{children}
		</StaticBootstrapContext.Provider>
	);
};
